// src/AgencyManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext'; // (1. Import useAuth - ถูกต้อง)
import toast from 'react-hot-toast';

const API_URL = 'https://rys-backend.onrender.com/api';

// --- (2. ลบฟังก์ชัน getAuthHeaders เก่า (บรรทัด 11-19) ทิ้งไป) ---
// const getAuthHeaders = () => {
//   ... (โค้ดเก่าที่ใช้ user.token) ...
// };
// --------------------------------------------------------

function AgencyManagement() {
  const navigate = useNavigate();
  // --- (3. ดึง getAuthHeaders "ตัวใหม่" มาจาก useAuth) ---
  const { user, getAuthHeaders } = useAuth();

  const [agencies, setAgencies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // (อัปเกรด) States สำหรับฟอร์ม
  const [agencyName, setAgencyName] = useState('');
  const [selectedMinistryId, setSelectedMinistryId] = useState(''); 
  const [editingAgencyId, setEditingAgencyId] = useState(null);

  const [ministries, setMinistries] = useState([]); // (State สำหรับเก็บกระทรวง)

  const [isDeleteAgencyModalOpen, setIsDeleteAgencyModalOpen] = useState(false);
  const [deletingAgency, setDeletingAgency] = useState(null);

  // --- (4. แก้ไข fetchData) ---
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const authHeaders = getAuthHeaders(); // (เรียกใช้ฟังก์ชันจาก useAuth)
      const [agenciesRes, ministriesRes] = await Promise.all([
        axios.get(`${API_URL}/agencies`, authHeaders), // (ส่ง authHeaders)
        axios.get(`${API_URL}/ministries`, authHeaders) // (ส่ง authHeaders)       
      ]);
      setAgencies(agenciesRes.data);
      setMinistries(ministriesRes.data);
    } catch (err) {
      toast.error("ไม่สามารถโหลดข้อมูลได้");
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders]); // (5. เพิ่ม getAuthHeaders ใน dependency)

  useEffect(() => {
    fetchData();
  }, [fetchData]); // (fetchData ถูกสร้างด้วย useCallback แล้ว)

  const clearForm = () => {
    setAgencyName('');
    setSelectedMinistryId(''); 
    setEditingAgencyId(null);
  };

  // (อัปเกรด) ปรับ handleSubmit ให้ส่ง ministry_id
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agencyName || !selectedMinistryId) {
      toast.error("กรุณากรอกชื่อหน่วยงาน และเลือกกระทรวง");
      return;
    }
    
    const payload = { 
      name: agencyName,
      ministry_id: parseInt(selectedMinistryId) 
    };
    
    const method = editingAgencyId ? 'put' : 'post';
    const url = editingAgencyId 
      ? `${API_URL}/agencies/${editingAgencyId}` 
      : `${API_URL}/agencies`;

    try {
      // (6. เรียกใช้ getAuthHeaders() จาก useAuth)
      const res = await axios({ method, url, data: payload, ...getAuthHeaders() });
      toast.success(res.data.msg);
      clearForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'ดำเนินการไม่สำเร็จ');
    }
  };

  // (อัปเกรด) ปรับ handleEditClick ให้ตั้งค่า ministry_id
  const handleEditClick = (agency) => {
    setEditingAgencyId(agency.id);
    setAgencyName(agency.name);
    setSelectedMinistryId(agency.ministry_id); 
  };

  // (ฟังก์ชัน Modal ลบ)
  const handleOpenDeleteModal = (agency) => {
    setDeletingAgency(agency);
    setIsDeleteAgencyModalOpen(true);
  };
  
  // --- (7. แก้ไข handleConfirmDelete) ---
  const handleConfirmDelete = async () => {
    if (!deletingAgency) return;
    try {
      // (เรียกใช้ getAuthHeaders() จาก useAuth)
      const res = await axios.delete(`${API_URL}/agencies/${deletingAgency.id}`, getAuthHeaders());
      toast.success(res.data.msg);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'ลบไม่สำเร็จ');
    } finally {
      setIsDeleteAgencyModalOpen(false);
      setDeletingAgency(null);
    }
  };
  
  if (user?.role !== 'admin') {
    navigate('/');
    return null;
  }

  return (
    <>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        <button
          onClick={() => navigate('/admin/feeds')}
          className="mb-6 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          &larr; ย้อนกลับ
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* (ฟอร์ม Agency - อัปเกรดแล้ว) */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-xl p-8 sticky top-28">
              <h2 className="text-2xl font-semibold text-gray-800 mb-6">
                {editingAgencyId ? 'แก้ไขหน่วยงาน' : 'เพิ่มหน่วยงานใหม่'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* (เพิ่ม Dropdown กระทรวง) */}
                <div>
                  <label htmlFor="ministry" className="block text-base font-medium text-gray-700">สังกัดกระทรวง *</label>
                  <select
                    id="ministry"
                    value={selectedMinistryId}
                    onChange={(e) => setSelectedMinistryId(e.target.value)}
                    required
                    className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm bg-white"
                  >
                    <option value="">-- เลือกกระทรวง --</option>
                    {ministries.map((min) => (
                      <option key={min.id} value={min.id}>{min.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="agencyName" className="block text-base font-medium text-gray-700">ชื่อหน่วยงาน (เช่น "กรมศุลกากร") *</label>
                  <input
                    type="text" id="agencyName" value={agencyName}
                    onChange={(e) => setAgencyName(e.target.value)}
                    required
                    className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm"
                  />
                </div>
                
                <button
                  type="submit"
                  className={`w-full px-4 py-3 font-semibold text-xl text-white rounded-md ${editingAgencyId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                >
                  {editingAgencyId ? 'บันทึกการแก้ไข' : 'เพิ่มหน่วยงาน'}
                </button>
                {editingAgencyId && (
                  <button type="button" onClick={clearForm} className="w-full mt-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-md">
                    ยกเลิก
                  </button>
                )}
              </form>
            </div>
          </div>
          
          {/* (ตาราง Agency - อัปเกรดแล้ว) */}
          <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-xl">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4 border-b pb-2">หน่วยงานที่มีอยู่</h3>
            {isLoading ? <p>Loading...</p> : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">หน่วยงาน</th>
                    <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">กระทรวง</th> 
                    <th className="px-6 py-3 text-right text-base font-medium text-gray-500 uppercase">ดำเนินการ</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {agencies.map((agency) => (
                    <tr key={agency.id}>
                      <td className="px-6 py-4 text-lg font-medium">{agency.name}</td>
                      <td className="px-6 py-4 text-lg text-gray-600">{agency.ministry_name}</td> 
                      <td className="px-6 py-4 text-right space-x-4">
                        <button onClick={() => handleEditClick(agency)} className="text-blue-600 hover:text-blue-900">แก้ไข</button>
                        <button onClick={() => handleOpenDeleteModal(agency)} className="text-red-600 hover:text-red-900">ลบ</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* (Modal ลบ Agency) */}
      {isDeleteAgencyModalOpen && deletingAgency && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-xl m-4">
            <h2 className="text-2xl font-semibold mb-5 text-gray-800 border-b pb-3">ยืนยันการลบหน่วยงาน</h2>
            <p className="mb-6 text-lg">
              คุณแน่ใจหรือไม่ว่าต้องการลบ: <strong className="text-red-600">{deletingAgency.name}</strong>?
            </p>
            <p className="text-base text-gray-600">
              (หมายเหตุ: จะลบได้ต่อเมื่อไม่มี "หัวข้อ" (Topics) ผูกอยู่)
            </p>
            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={() => setIsDeleteAgencyModalOpen(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete} // (เรียกฟังก์ชันที่ถูกต้อง)
                className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                ยืนยันการลบ
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AgencyManagement;